import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Icon } from "@/react/components/shared/Icon";
import { Button } from "@/react/components/shared/uikit/Button";
import { Form } from "@/react/components/shared/uikit/Form";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { emptyTestimonial } from "../../../hooks/usePaywallFormDefaultValues";
import { useCheckoutTabContext } from "../../CheckoutTabContext";
import { localeRoot } from "../../constants";
import { Container } from "../components/Container";
import { Header } from "../components/Header";
import { TestimonialForm } from "./Forms/TestimonialForm";

const dataTestId = "paywall-customization-testimonial-container";

export const TestimonialProperties = () => {
  const { watch } = useFormContext();
  const { testimonialPropertiesForm } = useCheckoutTabContext();

  const { fields, append, remove } = testimonialPropertiesForm || {
    fields: [],
    append: () => {},
    remove: () => {},
  };

  const isEnabled = watch(
    "paywall_customization_attributes.testimonial_properties.enabled",
  );
  const hasReachedLimit = fields.length >= 10;
  const hasReachedMinimum = fields.length <= 1;

  useEffect(() => {
    const component = document.querySelector(`[data-testid='${dataTestId}']`);
    if (!isEnabled) {
      component && component.setAttribute("inert", "");
    } else {
      component && component.removeAttribute("inert");
    }
  }, [isEnabled]);

  return (
    <>
      <Header className="flex flex-row items-center justify-between px-5 py-4">
        <Typography.LabelMd weight="semibold">
          {t([localeRoot, "builder.testimonials.show_testimonials"])}
        </Typography.LabelMd>
        <Form.ToggleSwitch
          name="paywall_customization_attributes.testimonial_properties.enabled"
          variant="large"
        />
      </Header>

      <Container className="overflow-y-auto px-0 pb-32" dataTestId={dataTestId}>
        <div
          className={classNames("divide-primary space-y-5 divide-y", {
            "pointer-events-none opacity-50": !isEnabled,
          })}
        >
          {fields.map((field, index: number) => (
            <div
              key={field.id}
              className={classNames("flex flex-col gap-y-2 px-6", {
                "pt-5": index > 0 && index < fields.length,
              })}
            >
              <div className="flex flex-row justify-between">
                <Typography.LabelSm weight="semibold">
                  {t("item")}
                </Typography.LabelSm>
                <div className="flex flex-row gap-x-2">
                  <Button
                    type="button"
                    variant="circle-plain"
                    disabled={hasReachedMinimum}
                    className="hover:text-dark !hover:bg-primary !p-1"
                    onClick={() => remove(index)}
                  >
                    <Icon
                      type="16-trash-bin"
                      viewBox="0 0 16 16"
                      className="!h-4 !w-4"
                    />
                  </Button>
                  <Button
                    disabled={hasReachedLimit}
                    variant="circle-plain"
                    className="hover:text-dark !hover:bg-primary !p-1"
                    type="button"
                    onClick={() => append({ ...field })}
                  >
                    <Icon
                      type="16-copy"
                      viewBox="0 0 16 16"
                      className="!h-4 !w-4"
                    />
                  </Button>
                </div>
              </div>
              <TestimonialForm
                keyFormId={field.id}
                name={`paywall_customization_attributes.testimonial_properties.testimonials.${index}`}
                disabled={!isEnabled}
              />
            </div>
          ))}
        </div>
        {!hasReachedLimit && (
          <div className="pl-6">
            <Button
              disabled={!isEnabled}
              type="button"
              onClick={() => append(emptyTestimonial)}
            >
              <Icon type="20-plus-v2" />
              {t([localeRoot, "builder.testimonials.add_item"])}
            </Button>
          </div>
        )}
      </Container>
    </>
  );
};
